import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

/**
 * The slice's whole HTTP surface (`docs/standards/code-organization.md` — `api.ts` is the
 * frontend's `repository.ts`).
 *
 * ## Why this slice is `event-tickets` and the endpoint is `event-check-in`
 *
 * The folder names usually pair one-to-one across the wire, and this one deliberately does not.
 * The server's domain is the **door**: `event-check-in` owns the tokens, the scan, the window and
 * the audit trail, because those are one set of rules and splitting them would put the same
 * invariant in two places. The member's side of it is a **ticket** — one screen, read-only, held
 * up in a queue — and naming the folder after the server's aggregate would tell the next reader
 * that check-in lives here. It does not, and `EVT-031` AC-6 is emphatic that it must not: see
 * `constants.ts`'s `SELF_CHECK_IN_IS_NOT_BUILT`.
 *
 * ## Why there is no id in the path
 *
 * `/ticket/mine`, exactly as `event-registrations` does it. **The member is the session**, so
 * there is no member id to supply and none to tamper with — `MEM-022` AC-7's "readable only by
 * the member who owns it" is true by construction rather than by a check the server could forget
 * to run. The route's own comment in `apps/api/src/domains/event-check-in/routes.ts` says the
 * same thing from the other side.
 *
 * ## What is not here, and will not be
 *
 * **No check-in mutation.** `EVT-031` AC-6 refuses member self-check-in outright, so the absence
 * is a decision rather than a gap — and a `POST` added to this file would be the first half of a
 * feature the story says is not built. The door check-in call lives in the organizer console
 * (`apps/organizer/src/domains/check-in/api.ts`), where the person pressing it is staff.
 *
 * **No order or receipt call.** `MEM-022` AC-3 to AC-7 need payments and orders, which do not
 * exist yet. The ticket is the half of the story a member can actually use today.
 */

/**
 * The two tokens the API mints for one registrant.
 *
 * Both are 43-character base64url strings. **Only `ticketToken` belongs in the QR** — the connect
 * token is a different code for a different purpose (`MEM-EVT`), and `constants.ts`'s `qrPayload`
 * is typed so that it cannot see the connect token at all.
 */
export interface MemberTicketTokens {
  readonly ticketToken: string;
  readonly connectToken: string;
}

const ticketKey = (eventId: string): readonly unknown[] => ['event-tickets', 'event', eventId];

/**
 * `MEM-022` AC-2's token, for this member and this event.
 *
 * `enabled` is how the caller keeps this from firing for a member with no registration: the API
 * answers `404` for one (and for a cancelled registration, which `MEM-017` AC-6 already makes the
 * same answer), and asking anyway would trade a rendered empty state for a failed request. It is
 * a courtesy, not a control — the server decides who has a ticket.
 *
 * **`staleTime` is infinite because the token is stable by construction.** The service mints once
 * per registrant per kind and returns the same value thereafter, precisely so that a member who
 * opens their ticket at home and again in the queue is holding the same code both times. Nothing
 * is gained by re-asking, and the queue is where the wifi is worst.
 *
 * `retry: false` for the reason the registrations slice gives: a 401 or a 404 here is a final
 * answer, and retrying it three times only delays the message.
 */
export function useMyTicket(
  eventId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<MemberTicketTokens> {
  return useQuery({
    queryKey: ticketKey(eventId),
    queryFn: () =>
      apiRequest<MemberTicketTokens>(
        `/api/event-check-in/events/${encodeURIComponent(eventId)}/ticket/mine`,
      ),
    enabled: options.enabled && eventId.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}
